import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, fmtEUR, SERVICE_TYPE_LABEL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import Pagination from "../components/Pagination";
import PageHeader from "../components/PageHeader";

const empty = { client_id: "", type: "dominio", description: "", amount: "", start_date: new Date().toISOString().slice(0, 10), billing: "yearly", work_entries: [] };
const emptyEntry = () => ({ description: "", hours: "", work_date: new Date().toISOString().slice(0, 10) });

export default function Servicos() {
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/services", { params: { page, type: typeFilter } }).then(r => setData(r.data));
  const loadClients = () => api.get("/clients/all").then(r => setClients(r.data));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, typeFilter]);
  useEffect(() => { loadClients(); }, []);

  const openNew = () => { setForm({ ...empty, work_entries: [], start_date: new Date().toISOString().slice(0,10) }); setEditing(null); setOpen(true); };
  const openEdit = async (s) => {
    // Load full service (with work_entries) for edit
    let full = s;
    try {
      const { data } = await api.get(`/services/${s.id}`);
      full = data;
    } catch {}
    setForm({ ...empty, ...full, amount: String(full.amount), start_date: full.start_date || "", work_entries: full.work_entries || [] });
    setEditing(s.id); setOpen(true);
  };

  const addEntryRow = () => setForm(f => ({ ...f, work_entries: [...(f.work_entries || []), emptyEntry()] }));
  const updateEntryRow = (idx, field, val) => setForm(f => {
    const arr = [...(f.work_entries || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...f, work_entries: arr };
  });
  const removeEntryRow = (idx) => setForm(f => ({ ...f, work_entries: (f.work_entries || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.client_id) return toast.error("Selecione um cliente");
    if (!form.description) return toast.error("Indique a descrição");
    if (!form.amount) return toast.error("Indique o valor");
    const payload = { ...form, amount: parseFloat(form.amount) };
    if (form.type !== "prestacao") {
      payload.billing = "yearly";
      delete payload.work_entries;
    } else {
      // Clean up empty rows and parse hours
      payload.work_entries = (form.work_entries || [])
        .filter(w => w.description && w.hours)
        .map(w => ({ description: w.description, hours: parseFloat(w.hours), work_date: w.work_date || null }));
    }
    try {
      if (editing) {
        await api.put(`/services/${editing}`, payload);
        toast.success("Serviço atualizado");
      } else {
        await api.post("/services", payload);
        toast.success("Serviço criado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const confirmDel = async () => {
    try { await api.delete(`/services/${delTarget.id}`); toast.success("Serviço eliminado"); setDelTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const expiringSoon = (s) => {
    if (!s.expires_at) return false;
    const d = new Date(s.expires_at);
    const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 15 && diff >= 0;
  };

  return (
    <div data-testid="servicos-page">
      <PageHeader title="Serviços" subtitle="Domínios, alojamento e prestação de serviços" testid="servicos-header"
        actions={<Button onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="add-servico-btn"><Plus size={16} /> Novo Serviço</Button>} />

      <div className="flex gap-2 mb-4">
        {[
          { v: "", l: "Todos" }, { v: "dominio", l: "Domínio" }, { v: "alojamento", l: "Alojamento" }, { v: "prestacao", l: "Prestação" }
        ].map(t => (
          <button key={t.v} onClick={() => { setTypeFilter(t.v); setPage(1); }} data-testid={`filter-${t.v || 'all'}`}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition ${typeFilter === t.v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="a4w-card overflow-hidden">
        <table className="w-full text-sm" data-testid="servicos-table">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-3 a4w-label">Cliente</th>
              <th className="px-4 py-3 a4w-label">Tipo</th>
              <th className="px-4 py-3 a4w-label">Descrição</th>
              <th className="px-4 py-3 a4w-label">Valor</th>
              <th className="px-4 py-3 a4w-label">Início</th>
              <th className="px-4 py-3 a4w-label">Expiração</th>
              <th className="px-4 py-3 a4w-label">Faturação</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(s => (
              <tr key={s.id} className="border-t border-slate-100 a4w-table-row">
                <td className="px-4 py-3 font-medium text-slate-800">{s.client_name}</td>
                <td className="px-4 py-3"><Badge variant="outline">{SERVICE_TYPE_LABEL[s.type]}</Badge></td>
                <td className="px-4 py-3 max-w-[260px] truncate">{s.description}</td>
                <td className="px-4 py-3 font-mono">{fmtEUR(s.amount)}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(s.start_date)}</td>
                <td className="px-4 py-3">
                  {s.expires_at ? (
                    <span className={expiringSoon(s) ? "text-orange-700 font-semibold" : ""}>{fmtDate(s.expires_at)}</span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">{s.billing === "yearly" ? <Badge style={{background:"#DBEAFE", color:"#1E3A8A"}}>Anual</Badge> : <Badge variant="outline">Única</Badge>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/servicos/${s.id}`}><Button size="icon" variant="ghost" data-testid={`view-servico-${s.id}`}><ExternalLink size={16} /></Button></Link>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-servico-${s.id}`}><Pencil size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setDelTarget(s)} data-testid={`del-servico-${s.id}`}><Trash2 size={16} className="text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500">Sem serviços. Clique em "Novo Serviço".</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="servico-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Serviço" : "Novo Serviço"}</DialogTitle>
            <DialogDescription>Serviços de Domínio/Alojamento são sempre anuais e geram automaticamente uma Nota de Encomenda (agrupada se já existir uma pendente do mesmo cliente).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="servico-cliente"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name || "(sem nome)"} {c.nif ? `· NIF ${c.nif}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Serviço</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, billing: v === "prestacao" ? form.billing : "yearly" })}>
                <SelectTrigger data-testid="servico-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dominio">Domínio</SelectItem>
                  <SelectItem value="alojamento">Alojamento Web</SelectItem>
                  <SelectItem value="prestacao">Prestação de Serviços</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Faturação</Label>
              <Select value={form.billing} onValueChange={(v) => setForm({ ...form, billing: v })} disabled={form.type !== "prestacao"}>
                <SelectTrigger data-testid="servico-billing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yearly">Anual</SelectItem>
                  <SelectItem value="once">Pagamento Único</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="servico-desc" placeholder={form.type === "prestacao" ? "Resumo geral do serviço (ex: Manutenção mensal)" : ""} />
            </div>
            <div>
              <Label>Valor (EUR)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="servico-amount" />
            </div>
            <div>
              <Label>Data de Início</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="servico-date" />
            </div>

            {form.type === "prestacao" && (
              <div className="col-span-2 border-t border-slate-200 pt-4 mt-2" data-testid="work-entries-inline">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-base">Registo de Horas</Label>
                    <p className="text-xs text-slate-500">Adicione linhas com o trabalho desenvolvido e respetivas horas. Será incluído no PDF de relatório.</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addEntryRow} data-testid="inline-add-entry">
                    <Plus size={14} /> Adicionar linha
                  </Button>
                </div>
                {(form.work_entries || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic py-4 text-center border border-dashed rounded-md">Sem linhas. Clique em "Adicionar linha" para começar.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
                      <div className="col-span-3">Data</div>
                      <div className="col-span-6">Descrição da tarefa</div>
                      <div className="col-span-2">Horas</div>
                      <div className="col-span-1"></div>
                    </div>
                    {(form.work_entries || []).map((w, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <Input type="date" className="col-span-3" value={w.work_date || ""} onChange={(e) => updateEntryRow(idx, "work_date", e.target.value)} data-testid={`inline-entry-date-${idx}`} />
                        <Input className="col-span-6" placeholder="Trabalho realizado..." value={w.description} onChange={(e) => updateEntryRow(idx, "description", e.target.value)} data-testid={`inline-entry-desc-${idx}`} />
                        <Input type="number" step="0.25" className="col-span-2" placeholder="0.00" value={w.hours} onChange={(e) => updateEntryRow(idx, "hours", e.target.value)} data-testid={`inline-entry-hours-${idx}`} />
                        <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => removeEntryRow(idx)} data-testid={`inline-entry-remove-${idx}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </div>
                    ))}
                    <div className="text-right text-xs text-slate-600 pt-2 border-t mt-2">
                      Total de horas: <span className="font-bold font-mono">
                        {(form.work_entries || []).reduce((acc, w) => acc + (parseFloat(w.hours) || 0), 0).toFixed(2)} h
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="servico-cancel">Cancelar</Button>
            <Button onClick={save} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="servico-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar serviço?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser revertida.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDel} className="bg-red-600 hover:bg-red-700" data-testid="servico-del-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
